import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { PressKitReleaseWithId } from '../types'

interface PressKitReleasesSectionProps {
  releases: PressKitReleaseWithId[]
}

function formatDate(ts: unknown): string {
  if (!ts) return ''
  const d =
    typeof (ts as { toDate?: () => Date }).toDate === 'function'
      ? (ts as { toDate: () => Date }).toDate()
      : new Date(ts as string | number)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function ReleaseItem({ release }: { release: PressKitReleaseWithId }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <article className="rounded-lg border overflow-hidden">
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground mb-1">{formatDate(release.publishedAt)}</p>
            <h3 className="text-base font-semibold leading-snug">{release.title}</h3>
            {!expanded && (
              <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{release.summary}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 mt-0.5"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <span className="ml-1 text-xs">{expanded ? 'Collapse' : 'Read more'}</span>
          </Button>
        </div>

        {expanded && (
          <div className="mt-4 prose prose-sm dark:prose-invert max-w-none text-muted-foreground [&_a]:text-foreground [&_a]:underline [&_strong]:text-foreground border-t pt-4">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{release.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </article>
  )
}

export function PressKitReleasesSection({ releases }: PressKitReleasesSectionProps) {
  return (
    <section className="space-y-4">
      <div className="space-y-3">
        {releases.map((release) => (
          <ReleaseItem key={release.id} release={release} />
        ))}
      </div>
    </section>
  )
}
